import { useState } from "react";

import { Popover, SeparatorList } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";

import { ExternalAccountAction } from "../dashboard/ExternalAccounts/ExternalAccountRow/ExternalAccountRow";

import styles from "./AccountsModule.module.scss";
import { useAccountActions, getAccountSubtitleParts } from "./AccountCard";

import type { AccountClassType } from "byzantine/src/Account";
import type { AddAccountMode } from "../../../components/settings/linked_accounts/types";

interface AccountListRowProps {
  account: AccountClassType;
  isEditing?: boolean;
  isHidden?: boolean;
  isSmall?: boolean;
  onVerify?: (account: AccountClassType) => void;
  onTriggerInstantVerification?: (mode: AddAccountMode) => void;
}

const AccountListRow = ({
  account,
  isEditing = false,
  isHidden = false,
  isSmall = false,
  onVerify,
  onTriggerInstantVerification,
}: AccountListRowProps) => {
  const { handleFavorite, handleToggleHidden } = useAccountActions(account);
  const { l10n } = useLocalization();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const name = account.getName();
  const subtitleParts = getAccountSubtitleParts(account);
  const balance = account.displayBalance();
  const isInternal = account.isInternal();
  const isClickable = isInternal && !isEditing;

  const navigateToAccount = () => {
    if (isClickable) window.location.assign(`/accounts/${account.id}`);
  };

  let actionButton = null;
  if (isEditing && isInternal && !isSmall) {
    actionButton = (
      <button
        type="button"
        className={`${styles.actionButton} ${styles.actionButtonVisible}`}
        onClick={handleToggleHidden}
        aria-label={isHidden ? "Unhide account" : "Hide account"}
      >
        <span className={isHidden ? "narmi-icon-eye-off" : "narmi-icon-eye"} />
      </button>
    );
  } else if (!isEditing && isInternal) {
    actionButton = (
      <button
        type="button"
        className={`${styles.starButton} ${
          account.favorited ? styles.starButtonActive : ""
        }`}
        onClick={handleFavorite}
        // Necessary to prevent `onKeyDown` navigation behavior in the parent
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={
          account.favorited ? "Unfavorite account" : "Favorite account"
        }
      >
        <span
          className={
            account.favorited ? "narmi-icon-star-filled" : "narmi-icon-star"
          }
        />
      </button>
    );
  } else if (!isInternal && !isSmall) {
    actionButton = (
      <Popover
        isOpen={isMenuOpen}
        onUserDismiss={() => setIsMenuOpen(false)}
        content={
          <div className={styles.kebabMenu}>
            <button
              type="button"
              className={`${styles.kebabMenuItem} button--reset`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleHidden(e);
                setIsMenuOpen(false);
              }}
            >
              <span
                className={isHidden ? "narmi-icon-eye-off" : "narmi-icon-eye"}
              />
              {isHidden
                ? l10n.getString("accounts-unhide-account")
                : l10n.getString("accounts-hide-from-view")}
            </button>
          </div>
        }
      >
        <button
          type="button"
          className={`${styles.kebabButton} button--reset`}
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen((prev) => !prev);
          }}
          aria-label={l10n.getString("accounts-more-options")}
        >
          <span className="narmi-icon-more-vertical" />
        </button>
      </Popover>
    );
  }

  return (
    <div
      className={`${styles.listRow} ${
        !isClickable ? styles.listRowNotClickable : ""
      }`}
      onClick={navigateToAccount}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          navigateToAccount();
        }
      }}
    >
      <div className={styles.listRowLeft}>
        <span className={styles.listRowName}>{name}</span>
        {subtitleParts.length > 0 && (
          <div className={styles.listRowNumber}>
            <SeparatorList items={subtitleParts} separator="•" />
          </div>
        )}
      </div>
      <div className={styles.listRowRight}>
        {isInternal || !onVerify || !onTriggerInstantVerification ? (
          <span className={styles.listRowBalance}>{balance}</span>
        ) : (
          <ExternalAccountAction
            account={account}
            onVerify={onVerify}
            onTriggerInstantVerification={onTriggerInstantVerification}
            balanceVariant="row"
          />
        )}
        {actionButton}
      </div>
    </div>
  );
};

export default AccountListRow;
